/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 03-Aug-2026
 * @author      : Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Automation Plan Engine A2 — build-spec §7 (D45) — Provider Web
 * Purpose      : REST client for AutomationPlanMasterApi (provider-api). Returns the same
 *                discriminated ApiResult the Communication Management screens use, so a 403 from
 *                the server can never be rendered as an empty catalog.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * A2 §7          | 03-Aug-2026 | Aravindan      | Initial creation
 * A2 §7          | 04-Aug-2026 | Aravindan      | deleteMaster — DELETE …/versions/{version}, 204
 */

import {
  executeDeleteData,
  executeGetData,
  executePostData,
  executePutData,
} from '../services/ApiBase';
import { ConfigUrl } from '../auth/OidcConfig';
import { ApiResult } from '../communications/MessageTemplateService';
import {
  CreateDraftRequest,
  PlanMasterSummaryDTO,
  PlanStepReadDTO,
  PlanStepUpsertDTO,
} from './AutomationTypes';

/**
 * `ApiResult` is IMPORTED from `MessageTemplateService`, not redeclared.
 *
 * Not merely to avoid duplication — `useAsyncResource`/`AsyncBoundary` in
 * `communications/AsyncStates` are typed against THAT module's `ApiResult`. A structurally
 * identical local copy would work until either definition gained a variant, at which point the
 * two would diverge and the screens here would silently stop handling the new state. One
 * definition, one set of state components.
 */
export type { ApiResult } from '../communications/MessageTemplateService';

const MASTERS_URL = `${ConfigUrl.baseURL}/automation-plans/masters`;
const TEMPLATES_CATALOG_URL = `${ConfigUrl.baseURL}/message-templates/catalog`;

/** One row of the plan-builder template picker (GET /message-templates/catalog?channel=…). */
export interface TemplateOption {
  templateMasterUid: string;
  name: string;
  channel: number;
  status: string;
  /** The template's authored content — carried by the catalog row so the builder can preview it. */
  subject?: string | null;
  body?: string | null;
  /**
   * SYSTEM / AUTOMATION / MANUAL — HOW the template is dispatched. Emitted by
   * `GET /message-templates/catalog` (`MessageTemplateApi.toSummaryDTO`). Optional because rows
   * written before the column existed read back null through a stale payload, and a null kind must
   * stay selectable rather than be treated as SYSTEM.
   */
  templateKind?: string | null;
}

/**
 * Usable message templates (APPROVED or PUBLISHED, latest per family) for one channel — feeds the
 * builder's Email/SMS template dropdowns so an author picks a template instead of typing its uid.
 */
export const fetchTemplateCatalog = async (
  channel: 'EMAIL' | 'SMS',
  accessToken: string,
): Promise<ApiResult<TemplateOption[]>> =>
  guard<TemplateOption[]>(
    () => executeGetData(`${TEMPLATES_CATALOG_URL}?channel=${channel}`, accessToken),
    (data) => data as TemplateOption[],
  );

/**
 * AUTHORIZATION — every endpoint below requires an authenticated caller.
 *
 * `AutomationPlanMasterApi` gates all six operations with the module-wide
 * `anyRequest().authenticated()` rule in `SecurityConfiguration` — gmscloud-provider-api has
 * exactly one provider-admin principal by design, so there is no further per-user authority check
 * (the earlier `PRV.ADM` / `provider_user_authority` grant table was removed). A tenant principal
 * still cannot reach these endpoints at all: it authenticates against a different deployable.
 *
 * `forbidden` is kept as a first-class result — rendered as its own state rather than an empty
 * grid — so any future 403 (e.g. a misconfigured token) still surfaces as a refusal, not silence.
 */
const classify = (res: { ok: boolean; status: number; message?: string }): ApiResult<never> => {
  switch (res.status) {
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'forbidden' };
    case 404:
      return { kind: 'notFound', message: res.message || 'Not found' };
    case 400:
    case 409:
      return {
        kind: 'rejected',
        status: res.status,
        message: res.message || 'The server rejected this request',
      };
    default:
      return {
        kind: 'error',
        status: typeof res.status === 'number' ? res.status : null,
        message: res.message || 'The request failed',
      };
  }
};

/**
 * Wraps an ApiBase call so a thrown transport error becomes a tagged error, never a throw.
 *
 * CORRECTED (PR 7871 review thread 66483). This block used to say `ApiBase.execute`'s catch does
 * `result.status = error.response.status` with no guard, so a network failure, CORS rejection or
 * timeout threw a TypeError straight out of `execute`. That WAS true and is now fixed — the catch
 * is optional-chained (`error?.response?.status ?? 0`), so `execute` honours its "always returns a
 * result" contract even when axios throws an error carrying no `.response`.
 *
 * The guard is deliberately KEPT, but its rationale has changed: it is defence in depth, not the
 * thing standing between a network blip and an unhandled rejection. It still converts a malformed
 * or unexpected payload into a tagged error, which is worth having. Do not restore the old claim.
 */
const guard = async <T>(
  call: () => Promise<any>,
  extract: (data: any) => T,
): Promise<ApiResult<T>> => {
  let res: any;
  try {
    res = await call();
  } catch (thrown: any) {
    return {
      kind: 'error',
      status: null,
      message: thrown?.message
        ? `Could not reach provider-api: ${thrown.message}`
        : 'Could not reach provider-api',
    };
  }
  if (res == null) {
    return { kind: 'error', status: null, message: 'provider-api returned no response' };
  }
  if (!res.ok) {
    return classify(res) as ApiResult<T>;
  }
  try {
    return { kind: 'ok', data: extract(res.data) };
  } catch (thrown: any) {
    // A 200 whose payload is not the declared shape is a server contract break, not "empty".
    return {
      kind: 'error',
      status: res.status ?? null,
      message: thrown?.message ?? 'provider-api returned a payload this screen cannot read',
    };
  }
};

const asArray = <T>(data: any, what: string): T[] => {
  if (Array.isArray(data)) return data as T[];
  throw new TypeError(`Expected a JSON array of ${what}`);
};

/**
 * GET /api/v1/automation-plans/masters
 *
 * Every master, EVERY status (DRAFT/PUBLISHED/SUPERSEDED/RETIRED), soft-deleted rows filtered out
 * server-side. This is the provider AUTHORING view and is distinct from the tenant-facing
 * `listForkable()`, which returns PUBLISHED only.
 *
 * Two things this response does NOT carry, both worth knowing before building on it:
 *
 *  1. NO STEPS. `PlanMasterSummaryDTO` is five fields — planMasterUid, version, name, description,
 *     status. Steps come from `fetchPlanMasterSteps`, one version at a time; this list will never
 *     carry them, so a screen that needs steps makes a second call per version.
 *  2. NO ORDERING GUARANTEE. It is `findAll()` through a JPA repository with no sort, so the order
 *     is whatever the database returns. The list screen sorts client-side, which is safe precisely
 *     because there is no server ordering to preserve (unlike the review queue, whose oldest-first
 *     order is load-bearing).
 */
export const fetchPlanMasters = async (
  accessToken: string,
): Promise<ApiResult<PlanMasterSummaryDTO[]>> =>
  guard<PlanMasterSummaryDTO[]>(
    () => executeGetData(MASTERS_URL, accessToken),
    (data) => asArray<PlanMasterSummaryDTO>(data, 'plan masters'),
  );

/**
 * GET /api/v1/automation-plans/masters/{uid}/versions/{version}/steps — the ONE version's steps,
 * as a bare JSON array in `stepOrder` order.
 *
 * This is the read that `replaceSteps` had nothing to replace FROM. Without it the editor opened
 * empty on a version that already had steps, and the first save wiped them — a whole-set replace
 * against a set the author could not see. Every caller must therefore treat a FAILURE here as
 * blocking, not as "no steps": an empty editor and a failed read are indistinguishable on screen
 * and only one of them is safe to save.
 *
 * The elements are the WIDE `PlanStepReadDTO`, not the narrow `PlanStepUpsertDTO` this client
 * writes — a persisted step may carry an anchor `scheduleMode` or a `USER` assignee that this app
 * does not author, and the read has to represent it rather than assume it away.
 */
export const fetchPlanMasterSteps = async (
  planMasterUid: string,
  version: number,
  accessToken: string,
): Promise<ApiResult<PlanStepReadDTO[]>> =>
  guard<PlanStepReadDTO[]>(
    () =>
      executeGetData(`${MASTERS_URL}/${planMasterUid}/versions/${version}/steps`, accessToken),
    (data) => asArray<PlanStepReadDTO>(data, 'plan steps'),
  );

/**
 * POST /api/v1/automation-plans/masters — creates version 1 in DRAFT.
 *
 * The controller reads a `Map<String, String>` with keys `name` and `contributedByTenant`, so this
 * body is intentionally flat strings and nothing else. `contributedByTenant` is omitted entirely
 * for provider-authored plans: it marks a plan PROMOTED from a tenant contribution, and sending a
 * blank string for it would record an empty attribution rather than none.
 *
 * A DRAFT is NOT forkable — no tenant sees it until `publish`.
 */
export const createDraft = async (
  request: CreateDraftRequest,
  accessToken: string,
): Promise<ApiResult<PlanMasterSummaryDTO>> => {
  const payload: Record<string, string> = { name: request.name };
  if (request.contributedByTenant != null && request.contributedByTenant.trim() !== '') {
    payload.contributedByTenant = request.contributedByTenant.trim();
  }
  // Plan-level fields carried on create (2026-08-13) — only non-blank values are sent; the backend
  // treats absent keys as null. This is what lets the unified builder create a whole plan in one Save.
  const planLevel: Record<string, string | null | undefined> = {
    description: request.description,
    leadSource: request.leadSource,
    entryStage: request.entryStage,
    nextPlanMasterUid: request.nextPlanMasterUid,
    goalKind: request.goalKind,
    goalParam: request.goalParam,
  };
  Object.entries(planLevel).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') {
      payload[k] = String(v).trim();
    }
  });
  return guard<PlanMasterSummaryDTO>(
    () => executePostData(MASTERS_URL, payload, accessToken),
    (data) => data as PlanMasterSummaryDTO,
  );
};

/**
 * POST /api/v1/automation-plans/masters/{uid}/publish — body `{"version": "<n>"}`.
 *
 * `version` is sent as a STRING because the controller binds a `Map<String, String>` and
 * `requireVersion` does `raw.trim()` before parsing. It is REQUIRED and there is no
 * "the current draft" resolver on the service — the caller must know which version it is acting on,
 * which is why the list row carries it.
 *
 * Publishing DEMOTES the currently published version to SUPERSEDED (uq_plan_master_one_published
 * permits exactly one). SUPERSEDED, not RETIRED, deliberately: RETIRED has a runtime meaning that
 * would stop live enrolments on existing forks. Only a DRAFT can be published — anything else is a
 * 400.
 */
export const publishVersion = async (
  planMasterUid: string,
  version: number,
  accessToken: string,
): Promise<ApiResult<PlanMasterSummaryDTO>> =>
  guard<PlanMasterSummaryDTO>(
    () => executePostData(`${MASTERS_URL}/${planMasterUid}/publish`, { version: String(version) }, accessToken),
    (data) => data as PlanMasterSummaryDTO,
  );

/**
 * POST /api/v1/automation-plans/masters/{uid}/retire — body `{"version": "<n>"}`.
 *
 * Stops NEW forks. Existing tenant forks and in-flight enrolments are explicitly unaffected — the
 * controller's own summary says so — so this is not a kill switch and must not be presented as one.
 * Retiring an already-retired version is a 400.
 */
export const retireVersion = async (
  planMasterUid: string,
  version: number,
  accessToken: string,
): Promise<ApiResult<PlanMasterSummaryDTO>> =>
  guard<PlanMasterSummaryDTO>(
    () => executePostData(`${MASTERS_URL}/${planMasterUid}/retire`, { version: String(version) }, accessToken),
    (data) => data as PlanMasterSummaryDTO,
  );

/**
 * POST /api/v1/automation-plans/masters/{uid}/versions — opens version N+1 as a DRAFT,
 * deep-copying the steps of the current PUBLISHED version.
 *
 * No body: the mapping declares no `consumes` and no `@RequestBody`. 404 when there is no PUBLISHED
 * version to base a new one on — so this is only offered on a PUBLISHED row.
 *
 * The deep copy is the ONLY way to get an existing version's steps onto an editable draft, given
 * there is no step read. It happens server-side and is not visible to this client.
 */
export const openNewVersion = async (
  planMasterUid: string,
  accessToken: string,
): Promise<ApiResult<PlanMasterSummaryDTO>> =>
  guard<PlanMasterSummaryDTO>(
    () => executePostData(`${MASTERS_URL}/${planMasterUid}/versions`, null, accessToken),
    (data) => data as PlanMasterSummaryDTO,
  );

/**
 * PUT /api/v1/automation-plans/masters/{uid}/versions/{version}/steps — body is a BARE ARRAY of
 * step DTOs, not an object wrapping one.
 *
 * WHOLE-SET REPLACE. Whatever is sent becomes the version's entire step set; anything omitted is
 * gone. There is no per-step PATCH, so a partial submission is data loss, not a partial update —
 * `fetchPlanMasterSteps` now makes the existing set visible to merge against, which is what makes
 * this endpoint usable for EDITING rather than only for first authoring.
 *
 * Four ways this 400s, all of them the caller's shape rather than a server fault:
 *   * the target version is not a DRAFT (a PUBLISHED version's steps are immutable — open a new
 *     version instead);
 *   * duplicate `stepOrder`;
 *   * a `nextStepUid` that matches no other step's `stepUid` in the SAME request (AU026) — these
 *     are client-correlation uuids resolved and then discarded server-side, never persisted;
 *   * any step whose `config` fails `AutomationStepConfigValidator` for its kind. That validator
 *     rejects UNKNOWN KEYS, so an extra field is a hard failure — which is why
 *     `buildStepConfig` emits closed per-kind key sets.
 *
 * The 200 body is the plan master SUMMARY (re-read after the write), not the steps that were saved.
 * To see what was persisted, re-read with `fetchPlanMasterSteps`.
 */
export const replaceSteps = async (
  planMasterUid: string,
  version: number,
  steps: PlanStepUpsertDTO[],
  accessToken: string,
): Promise<ApiResult<PlanMasterSummaryDTO>> =>
  guard<PlanMasterSummaryDTO>(
    () =>
      executePutData(
        `${MASTERS_URL}/${planMasterUid}/versions/${version}/steps`,
        steps,
        accessToken,
      ),
    (data) => data as PlanMasterSummaryDTO,
  );

/**
 * The plan-level attributes the widened PUT (2026-08-13) persists on a DRAFT: description plus the
 * targeting fields the unified builder edits. All optional — omitted/blank keys are left unchanged
 * server-side. `nextPlanMasterUid`/`specificDate` are strings on the wire (UUID / ISO date).
 */
export interface PlanMasterAttrs {
  description?: string | null;
  leadSource?: string | null;
  entryStage?: string | null;
  nextPlanMasterUid?: string | null;
  goalKind?: string | null;
  goalParam?: string | null;
  contactTypes?: string | null;
  specificDate?: string | null;
  businessDaysOnly?: boolean | null;
}

/**
 * PUT /api/v1/automation-plans/masters/{uid}/versions/{version} — persists a DRAFT's plan-level
 * attributes. Widened 2026-08-13 from description-only to the full {@link PlanMasterAttrs} set so the
 * unified provider builder can save lead source / trigger (entry stage + goal) / next plan too.
 * DRAFT-only — a published version's attributes are frozen (400 otherwise). 200 body is the SUMMARY.
 */
export const updatePlanAttributes = async (
  planMasterUid: string,
  version: number,
  attrs: PlanMasterAttrs,
  accessToken: string,
): Promise<ApiResult<PlanMasterSummaryDTO>> =>
  guard<PlanMasterSummaryDTO>(
    () =>
      executePutData(
        `${MASTERS_URL}/${planMasterUid}/versions/${version}`,
        attrs,
        accessToken,
      ),
    (data) => data as PlanMasterSummaryDTO,
  );

/**
 * DELETE /api/v1/automation-plans/masters/{uid}/versions/{version} — 204, NO BODY.
 *
 * A SOFT delete: the row is flagged deleted and drops out of `fetchPlanMasters` (which filters
 * deleted rows server-side), which is why the caller refreshes the list rather than patching the
 * grid — the disappearance is the server's answer, not an optimistic one.
 *
 * What it does NOT touch: tenant FORKS. A fork is a copy taken at fork time, so a tenant that
 * already forked this version keeps its plan and its running enrolments after the master is gone.
 * Deleting removes the version from the CATALOG only, and the confirm copy on the list says so —
 * "delete" reads terminal in a way this is not, the mirror of the problem `retireVersion` has.
 *
 * Typed `ApiResult<void>` because there is no payload to extract. `guard`'s extractor still runs on
 * the 204's empty `data` and returns `undefined`, so `kind: 'ok'` here means exactly "the server
 * accepted it" and nothing more — there is no re-read summary to report back, unlike publish/retire.
 */
export const deleteMaster = async (
  planMasterUid: string,
  version: number,
  accessToken: string,
): Promise<ApiResult<void>> =>
  guard<void>(
    () => executeDeleteData(`${MASTERS_URL}/${planMasterUid}/versions/${version}`, accessToken),
    () => undefined,
  );

/**
 * GET /api/v1/automation-plans/masters/{uid}/versions — the version history of one plan family,
 * every non-deleted version, any status, newest first. Added 2026-08-13 (Comms gap-fix #2): the
 * endpoint the note here used to record as missing now exists on `AutomationPlanMasterApi
 * .versionsForPlan`. Lets a screen group a plan's lineage instead of showing flat (uid, version)
 * rows. 404 when no plan master has this uid.
 */
export const fetchPlanMasterVersions = async (
  planMasterUid: string,
  accessToken: string,
): Promise<ApiResult<PlanMasterSummaryDTO[]>> =>
  guard<PlanMasterSummaryDTO[]>(
    () => executeGetData(`${MASTERS_URL}/${planMasterUid}/versions`, accessToken),
    (data) => asArray<PlanMasterSummaryDTO>(data, 'plan master versions'),
  );

/**
 * ==================== Notes on the API surface (kept honest) ====================
 *
 *  RESOLVED — read one version's steps (`GET …/versions/{version}/steps` = `fetchPlanMasterSteps`).
 *  The whole-set-replace caveat on `replaceSteps` still stands.
 *
 *  RESOLVED (2026-08-13) — PLAN-LEVEL UPSERT. `updatePlanAttributes` (PUT …/versions/{version}) was
 *  widened to persist the full set (description, leadSource, entryStage, nextPlanMasterUid, goalKind,
 *  goalParam, contactTypes, specificDate, businessDaysOnly) — a plan's trigger stage/lead
 *  source now save. `name` still has no writer outside `createDraft` (the builder shows it read-only
 *  in edit mode, editable only in create mode).
 *  (`color` was in that set until 2026-08-17, when the field was removed outright — column, DTO
 *  and model — rather than given a writer. See PlanMaster in AutomationTypes.)
 *
 *  RESOLVED (2026-08-13) — VERSION HISTORY PER PLAN is now `fetchPlanMasterVersions` above.
 *
 * Still NOT wired, on purpose: any ENROL endpoint. Enrolment is tenant-scoped (`AutomationPlanApi`,
 * tenant-api) and a provider has no recipients to enrol.
 * ============================================================================
 */
